// Outer principals — the client-side registry for egress-bearing agents.
//
// The CDS backend (products/diwa) still, correctly, refuses to grant `egress`: today no
// principal can hold it server-side. Leap 4 opens egress as a GATED OUTER capability, but the
// actual crossing runs later in the Diwa browser extension. So an Outer principal — an agent
// authorized to send OUT of your world, holding egress and NOTHING that reads your context —
// is modelled here, on the keeper's device, until that custody lands on the wire.
//
// Keeping Inner principals (the real /agent/principals) and Outer principals apart is itself
// the invariant made physical: the system that mints Inner keys refuses egress, and this
// registry only ever holds egress. Neither can produce a principal that crosses the membrane.

import { violatesSplit } from './capabilitySplit'

export interface OuterPrincipal {
  id: string
  name: string
  prefix: string
  scopes: string[]
  created_at: string
  last_used_at: string | null
  revoked: boolean
  /** Marks this as the client-side stub, distinct from a server-minted Inner principal. */
  outer: true
}

const KEY = 'diwa-outer-principals'
export const OUTER_EVENT = 'diwa-outer-principals-changed'

let memory: OuterPrincipal[] = []

function hasStorage(): boolean {
  try {
    return typeof localStorage !== 'undefined'
  } catch {
    return false
  }
}

function read(): OuterPrincipal[] {
  if (!hasStorage()) return memory
  try {
    const raw = localStorage.getItem(KEY)
    return raw ? (JSON.parse(raw) as OuterPrincipal[]) : []
  } catch {
    return []
  }
}

function write(list: OuterPrincipal[]): void {
  if (hasStorage()) {
    try {
      localStorage.setItem(KEY, JSON.stringify(list))
    } catch {
      /* ignore */
    }
  } else {
    memory = list
  }
  try {
    if (typeof window !== 'undefined') window.dispatchEvent(new Event(OUTER_EVENT))
  } catch {
    /* no window */
  }
}

export function listOuter(): OuterPrincipal[] {
  return read()
}

function randomKey(): { key: string; prefix: string } {
  const body = Array.from({ length: 32 }, () =>
    Math.floor(Math.random() * 36).toString(36),
  ).join('')
  const key = `dwo_${body}`
  return { key, prefix: key.slice(0, 8) }
}

/** Mint an Outer principal. Rejects any scope set that would cross the membrane (defence in
 *  depth — the mint form already prevents it) or that carries no egress at all. Returns the
 *  stored principal plus the one-time full key. */
export function mintOuter(
  name: string,
  scopes: string[],
): { principal: OuterPrincipal; key: string } {
  if (violatesSplit(scopes)) {
    throw new Error('An Outer agent cannot also hold a context scope — that would break the split.')
  }
  if (!scopes.includes('egress')) {
    throw new Error('An Outer principal must hold egress.')
  }
  const { key, prefix } = randomKey()
  const principal: OuterPrincipal = {
    id: `outer_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`,
    name,
    prefix,
    scopes,
    created_at: new Date().toISOString(),
    last_used_at: null,
    revoked: false,
    outer: true,
  }
  write([...read(), principal])
  return { principal, key }
}

export function revokeOuter(id: string): void {
  write(read().map((p) => (p.id === id ? { ...p, revoked: true } : p)))
}
